import { Box, Typography, makeStyles } from '@material-ui/core';
import { InfoCard, Link, Progress } from '@backstage/core-components';
import { Entity, stringifyEntityRef } from '@backstage/catalog-model';
import { useEntity } from '@backstage/plugin-catalog-react';
import { DeliveryRun, useEntityDelivery } from '../health/api';

export const isDeliveryAvailable = (entity: Entity) =>
  Boolean(entity.metadata.annotations?.['github.com/project-slug']);

const useStyles = makeStyles(theme => ({
  tiles: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))',
    gap: theme.spacing(2),
    marginBottom: theme.spacing(2),
  },
  value: { fontSize: 24, fontWeight: 600, lineHeight: 1.2 },
  label: { color: theme.palette.text.secondary, fontSize: 12 },
  section: {
    marginTop: theme.spacing(1.5),
    marginBottom: theme.spacing(0.5),
    fontWeight: 600,
  },
  row: {
    display: 'flex',
    gap: theme.spacing(1.5),
    fontSize: 13,
    padding: theme.spacing(0.25, 0),
  },
  sha: { fontFamily: 'monospace', minWidth: 72 },
  muted: { color: theme.palette.text.secondary },
  failed: { color: '#E5383B' },
}));

const formatDate = (iso: string) =>
  new Date(iso).toLocaleString('ru-RU', {
    day: '2-digit',
    month: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
  });

const formatHours = (h: number) =>
  h < 48 ? `${h.toLocaleString('ru-RU')} ч` : `${Math.round(h / 24)} дн`;

const Tile = ({ value, label }: { value: string; label: string }) => {
  const classes = useStyles();
  return (
    <Box>
      <Typography className={classes.value}>{value}</Typography>
      <Typography className={classes.label}>{label}</Typography>
    </Box>
  );
};

const RunRow = ({ run, failed }: { run: DeliveryRun; failed?: boolean }) => {
  const classes = useStyles();
  return (
    <div className={classes.row}>
      <Link to={run.url} className={classes.sha}>
        {run.sha}
      </Link>
      <span className={failed ? classes.failed : undefined}>
        {formatDate(run.finishedAt)}
      </span>
      <span className={classes.muted}>{run.workflow}</span>
    </div>
  );
};

/**
 * Релизы и деплои в прод за 30 дней. Деплой — успешная прод-сборка по push в основную ветку
 * (плагин kubernetes-diagnostics, /delivery).
 */
export const DeliveryCard = () => {
  const classes = useStyles();
  const { entity } = useEntity();
  const { value, error, loading } = useEntityDelivery(
    stringifyEntityRef(entity),
  );

  let content;
  if (loading) {
    content = <Progress />;
  } else if (!value) {
    content = (
      <Typography color="error">
        Не удалось получить данные: {error?.message}
      </Typography>
    );
  } else {
    const m = value.metrics;
    content = (
      <>
        <Box className={classes.tiles}>
          <Tile
            value={m.deploysPerWeek.toLocaleString('ru-RU')}
            label={`деплоев в неделю (${m.deploys} за ${m.windowDays} дн)`}
          />
          <Tile
            value={
              m.medianLeadTimeHours !== undefined
                ? formatHours(m.medianLeadTimeHours)
                : '—'
            }
            label="от коммита до прода (медиана)"
          />
          <Tile
            value={`${m.failedProdBuildsPct}%`}
            label={`упавших прод-сборок (${m.failedProdBuilds})`}
          />
        </Box>
        {value.recentDeploys.length ? (
          <>
            <Typography className={classes.section}>
              Последние деплои
            </Typography>
            {value.recentDeploys.slice(0, 5).map(r => (
              <RunRow key={r.url} run={r} />
            ))}
          </>
        ) : (
          <Typography className={classes.muted}>
            Прод-сборок в ветке {value.defaultBranch} за {m.windowDays} дн не
            было
          </Typography>
        )}
        {value.recentFailures.length > 0 && (
          <>
            <Typography className={classes.section}>Упавшие сборки</Typography>
            {value.recentFailures.slice(0, 3).map(r => (
              <RunRow key={r.url} run={r} failed />
            ))}
          </>
        )}
        {value.releases.length > 0 && (
          <>
            <Typography className={classes.section}>Релизы</Typography>
            {value.releases.map(r => (
              <div key={r.url} className={classes.row}>
                <Link to={r.url}>{r.name}</Link>
                <span className={classes.muted}>
                  {formatDate(r.publishedAt)}
                </span>
              </div>
            ))}
          </>
        )}
      </>
    );
  }

  return <InfoCard title="Релизы и деплои">{content}</InfoCard>;
};
